'use client';

import { useState } from 'react';
import { ReferenceImages } from './ReferenceImages';
import type { WavespeedParam } from '@/lib/ai/wavespeed';
import { StudioSelect } from './StudioSelect';
import { useI18n } from '@/contexts/I18nContext';

export type ParamDef = WavespeedParam;

function prettyLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const inputClass =
  'w-full rounded-xl border border-white/15 bg-white/10 px-2 py-1.5 text-sm text-white outline-none transition placeholder:text-white/40 focus:border-fuchsia-400/60 focus:ring-2 focus:ring-fuchsia-500/20';

export function DynamicParamField({
  param,
  value,
  onChange,
}: {
  param: ParamDef;
  value: any;
  onChange: (v: any) => void;
}) {
  const { t } = useI18n();
  const labelKey = `studio.param.${param.key}`;
  const translatedLabel = t(labelKey);
  const label = translatedLabel === labelKey ? prettyLabel(param.key) : translatedLabel;
  const hintKey = `studio.paramHint.${param.key}`;
  const translatedHint = t(hintKey);
  const hint = translatedHint === hintKey ? param.description : translatedHint;
  const req = param.required ? <span className="text-red-300 ml-0.5">*</span> : null;

  const labelEl = (
    <label className="mb-1 block text-[10px] uppercase tracking-wide text-white/60">
      {label}
      {req}
    </label>
  );

  if (param.widget === 'select' && param.enum) {
    return (
      <div>
        {labelEl}
        <StudioSelect
          value={value ?? param.default ?? param.enum[0]}
          onChange={(next) => onChange(param.type === 'integer' || param.type === 'number' ? Number(next) : next)}
          options={param.enum.map((e) => ({ value: String(e), label: String(e) }))}
        />
        {hint && <HintText text={hint} />}
      </div>
    );
  }

  if (param.widget === 'slider') {
    const n = Number(value ?? param.default ?? param.min ?? 0);
    const step = param.type === 'integer' ? 1 : (param.max! - param.min!) / 100;
    return (
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] uppercase tracking-wide text-white/60">
            {label}{req}
          </label>
          <span className="font-mono text-xs text-white/70">{n}</span>
        </div>
        <input
          type="range"
          min={param.min}
          max={param.max}
          step={step}
          value={n}
          onChange={(e) =>
            onChange(param.type === 'integer' ? parseInt(e.target.value, 10) : parseFloat(e.target.value))
          }
          className="w-full accent-fuchsia-500"
        />
        {hint && <HintText text={hint} />}
      </div>
    );
  }

  if (param.widget === 'number') {
    return (
      <div>
        {labelEl}
        <input
          type="number"
          value={value ?? param.default ?? ''}
          onChange={(e) =>
            onChange(
              e.target.value === ''
                ? undefined
                : param.type === 'integer'
                ? parseInt(e.target.value, 10)
                : parseFloat(e.target.value)
            )
          }
          className={inputClass}
        />
        {hint && <HintText text={hint} />}
      </div>
    );
  }

  if (param.widget === 'toggle') {
    const on = !!(value ?? param.default);
    return (
      <div className="flex items-center justify-between py-2">
        <div>
          <label className="text-[10px] uppercase tracking-wide text-white/60">
            {label}{req}
          </label>
          {hint && <HintText text={hint} />}
        </div>
        <button
          type="button"
          onClick={() => onChange(!on)}
          className={`relative w-10 h-5 rounded-full transition ${
            on ? 'bg-fuchsia-500' : 'bg-white/15'
          }`}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition ${
              on ? 'right-0.5' : 'left-0.5'
            }`}
          />
        </button>
      </div>
    );
  }

  if (param.widget === 'image-url' || param.widget === 'media-list') {
    const multiple = param.widget === 'media-list';
    return <div>{labelEl}<ReferenceImages
      value={multiple ? value ?? [] : value ? [value] : []}
      onChange={(items) => onChange(multiple ? items : items[0] ?? '')}
      max={multiple ? param.maxItems ?? 20 : 1} label={label}
      hint={hint} kind={param.mediaKind ?? 'image'}
    /></div>;
  }

  if (param.widget === 'json') {
    return <JsonParam value={value ?? param.default} onChange={onChange} label={labelEl} hint={hint} />;
  }

  if (param.widget === 'textarea') {
    return (
      <div>
        {labelEl}
        <textarea
          value={value ?? param.default ?? ''}
          onChange={(e) => onChange(e.target.value || undefined)}
          className="min-h-[80px] w-full resize-y rounded-xl border border-white/15 bg-white/10 px-2 py-1.5 text-sm text-white outline-none transition placeholder:text-white/40 focus:border-fuchsia-400/60 focus:ring-2 focus:ring-fuchsia-500/20"
        />
        {hint && <HintText text={hint} />}
      </div>
    );
  }

  // default: text input
  return (
    <div>
      {labelEl}
      <input
        type="text"
        value={value ?? param.default ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
        className={inputClass}
      />
      {hint && <HintText text={hint} />}
    </div>
  );
}

function HintText({ text }: { text: string }) {
  return <p className="mt-1 line-clamp-2 text-[10px] text-white/60">{text}</p>;
}

function JsonParam({ value, onChange, label, hint }: {
  value: any; onChange: (value: any) => void; label: React.ReactNode; hint?: string;
}) {
  const [text, setText] = useState(() => value === undefined ? '' : JSON.stringify(value));
  const [invalid, setInvalid] = useState(false);
  return <div>{label}<textarea className={inputClass} value={text} aria-invalid={invalid}
    onChange={(event) => {
      setText(event.target.value);
      try {
        const parsed = event.target.value.trim() ? JSON.parse(event.target.value) : undefined;
        if (parsed !== undefined && !Array.isArray(parsed)) throw new Error('Expected array');
        onChange(parsed); setInvalid(false);
      } catch { setInvalid(true); }
    }} />{hint && <HintText text={hint} />}</div>;
}
